<template>
    <div class="modal-mask">
        <div class="modal-wrapper">
            <div class="modal-container" style="width: 800px; max-width: 90vw; padding: 0;">
                <div class="modal-header" style="background-color: #274188; padding: 15px 25px !important; padding-bottom: 15px !important; border-top-left-radius: 2px; border-top-right-radius: 2px; display: flex; justify-content: space-between; align-items: center;">
                    <h2 style="margin: 0; color: white;">Clinical Readings</h2>
                    <button @click.stop="$emit('close')" class="red-close-button" style="margin: 0;">
                        <i class="pi pi-times" style="padding: 0;"></i>
                    </button>
                </div>
                
                <div style="padding: 20px;">
                    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
                        <div style="display: flex; gap: 8px; flex-wrap: wrap;">
                            <label style="display: block; font-weight: bold; width: 100%; margin-bottom: 5px;">Filter by Type</label>
                            
                            <Button 
                                label="All" 
                                :class="selectedType === null ? 'p-button-primary' : 'p-button-outlined p-button-secondary'"
                                style="font-weight: bold; border-radius: 20px; padding: 5px 15px; font-size: 0.9em;"
                                @click="selectedType = null; fetchReadings(1)"
                            />
                            
                            <Button v-for="option in typeOptions" :key="option.value"
                                :label="option.label" 
                                :class="selectedType === option.value ? 'p-button-primary' : 'p-button-outlined p-button-secondary'"
                                style="font-weight: bold; border-radius: 20px; padding: 5px 15px; font-size: 0.9em;"
                                @click="selectedType = option.value; fetchReadings(1)"
                            />
                        </div>
                        <div>
                            <Button 
                                icon="pi pi-refresh" 
                                class="p-button-rounded p-button-text p-button-plain" 
                                @click="fetchReadings(currentPage)" 
                                :disabled="loading"
                            />
                        </div>
                    </div>

                    <div v-if="loading" style="text-align: center; padding: 40px 0;">
                        <i class="pi pi-spin pi-spinner" style="font-size: 2rem;"></i>
                        <p>Loading readings...</p>
                    </div>
                    
                    <div v-else-if="readings.length === 0" style="text-align: center; padding: 40px 0; background: #f9f9f9; border-radius: 8px;">
                        <i class="pi pi-info-circle" style="font-size: 2rem; color: #888; margin-bottom: 10px;"></i>
                        <p>No clinical readings found.</p>
                    </div>
                    
                    <div v-else>
                        <div style="max-height: 400px; overflow-y: auto; overflow-x: auto; border: 1px solid #e8e8e8; border-radius: 8px; margin-bottom: 20px;">
                            <table style="width: 100%; border-collapse: collapse; text-align: left;">
                                <thead style="position: sticky; top: 0; background-color: #f1f1f1; z-index: 1;">
                                    <tr>
                                        <th style="padding: 12px; border-bottom: 2px solid #ddd; font-weight: bold; color: #333;">Date & Time</th>
                                        <th style="padding: 12px; border-bottom: 2px solid #ddd; font-weight: bold; color: #333;">Type</th>
                                        <th style="padding: 12px; border-bottom: 2px solid #ddd; font-weight: bold; color: #333;">Value</th>
                                        <th style="padding: 12px; border-bottom: 2px solid #ddd; font-weight: bold; color: #333;">Unit</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(reading, index) in readings" :key="reading.uuid" :style="{ backgroundColor: index % 2 === 0 ? '#fff' : '#fcfcfc', borderBottom: '1px solid #eee' }">
                                        <td style="padding: 12px; border-bottom: 1px solid #eee;">{{ reading.created_at }}</td>
                                        <td style="padding: 12px; border-bottom: 1px solid #eee; font-weight: 500;">{{ labelForType(reading.type) }}</td>
                                        <td style="padding: 12px; border-bottom: 1px solid #eee; font-weight: bold;">{{ reading.value }}</td>
                                        <td style="padding: 12px; border-bottom: 1px solid #eee; color: #666;">{{ reading.unit }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        
                        <!-- Pagination Controls -->
                        <div v-if="totalPages > 1" style="display: flex; justify-content: space-between; align-items: center; border-top: 1px solid #eee; padding-top: 15px;">
                            <p style="margin: 0; color: #666;">Showing {{ pagination.from || 0 }} to {{ pagination.to || 0 }} of {{ pagination.total || 0 }} entries</p>
                            <div style="display: flex; gap: 10px; align-items: center;">
                                <Button 
                                    icon="pi pi-angle-left" 
                                    label="Previous" 
                                    class="p-button-outlined p-button-sm" 
                                    @click="fetchReadings(currentPage - 1)" 
                                    :disabled="currentPage <= 1 || loading"
                                    style="padding: 5px 10px;"
                                />
                                <span style="font-weight: bold; padding: 0 10px;">Page {{ currentPage }} of {{ totalPages }}</span>
                                <Button 
                                    icon="pi pi-angle-right" 
                                    label="Next" 
                                    iconPos="right"
                                    class="p-button-outlined p-button-sm" 
                                    @click="fetchReadings(currentPage + 1)" 
                                    :disabled="currentPage >= totalPages || loading"
                                    style="padding: 5px 10px;"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { trendReadingOptions, readingLabelFor } from '@/services/readings';

export default {
    props: {
        patientId: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            loading: false,
            readings: [],
            selectedType: null,
            // Seeded from the local registry so the filters render immediately, then replaced by
            // kraken's own registry (/reading-types) on mount. Previously this list hardcoded the
            // type-to-label translation - notably that a BGL is stored as type 'insulin'.
            readingTypes: [],
            typeOptions: trendReadingOptions(),
            currentPage: 1,
            totalPages: 1,
            pagination: {
                from: 0,
                to: 0,
                total: 0
            }
        }
    },
    mounted() {
        this.fetchReadingTypes();
        this.fetchReadings(1);
    },
    methods: {
        // kraken owns the reading registry (App\Support\ReadingTypes). Fetch it so the filters and
        // labels can't drift from what the backend stores and validates; on failure the local
        // fallback in services/readings.js is already in place, so the view still works.
        fetchReadingTypes() {
            axios.post('/reading-types', {}, this.$store.state.header)
                .then(res => {
                    if (Array.isArray(res.data) && res.data.length) {
                        this.readingTypes = res.data;
                        this.typeOptions = trendReadingOptions(res.data);
                    }
                })
                .catch(err => {
                    console.error("Error fetching reading types, using local registry:", err);
                });
        },
        // The label a reading is displayed under - 'insulin' is stored but shown as 'BGL'.
        labelForType(type) {
            return readingLabelFor(type, this.readingTypes);
        },
        fetchReadings(page) {
            if (!this.patientId) return;
            
            this.loading = true;
            this.currentPage = page;
            
            const payload = {
                page: page
            };
            
            if (this.selectedType) {
                payload.type = this.selectedType;
            }
            
            axios.post(`/patient/${this.patientId}/readings`, payload, this.$store.state.header)
                .then(res => {
                    if (res.data) {
                        this.readings = res.data.data || [];
                        
                        if (res.data.meta) {
                            this.currentPage = res.data.meta.current_page || 1;
                            this.totalPages = res.data.meta.last_page || 1;
                            this.pagination = {
                                from: res.data.meta.from,
                                to: res.data.meta.to,
                                total: res.data.meta.total
                            };
                        }
                    } else {
                        this.readings = [];
                        this.totalPages = 1;
                    }
                })
                .catch(err => {
                    console.error("Error fetching clinical readings:", err);
                    this.$toast.add({severity:'error', summary: 'Error', detail: 'Failed to load clinical readings. Please try again.', life: 3000});
                })
                .finally(() => {
                    this.loading = false;
                });
        }
    }
}
</script>

<style scoped>
.modal-mask {
    position: fixed;
    z-index: 9998;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: table;
    transition: opacity 0.3s ease;
}

.modal-wrapper {
    display: table-cell;
    vertical-align: middle;
}

.modal-container {
    margin: 0px auto;
    padding: 20px 30px;
    background-color: #fff;
    border-radius: 2px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
    transition: all 0.3s ease;
    font-family: Helvetica, Arial, sans-serif;
}
</style>
